import { create } from 'zustand';
import {
  fetchCompanyInstalledApps,
  installCompanyApp,
  uninstallCompanyApp,
} from 'services/companyAppsService';
import { ClientHttpError } from 'utils/fetchUtils';
import { toUserFacingHttpFailureMessage } from 'utils/userFacingHttpErrors';
import { isAllCompaniesSelectionActive } from 'utils/allCompaniesMode';

export type CompanyInstalledAppsStatus =
  'idle' | 'loading' | 'success' | 'error';

type CompanyInstalledAppsState = {
  companyId: string | null;
  installedAppIds: string[];
  status: CompanyInstalledAppsStatus;
  loadError: string | null;
  mutatingAppId: string | null;
  mutatingMode: 'install' | 'uninstall' | null;
  ensureLoaded: (companyId: string | null) => Promise<void>;
  reload: (companyId: string | null) => Promise<void>;
  installApp: (companyId: string, appId: string) => Promise<void>;
  uninstallApp: (companyId: string, appId: string) => Promise<void>;
  reset: () => void;
};

function resolveScopedCompanyId(companyId: string | null): string | null {
  if (!companyId || isAllCompaniesSelectionActive(companyId)) {
    return null;
  }
  return companyId;
}

function toLoadErrorMessage(error: unknown): string {
  if (error instanceof ClientHttpError) {
    return toUserFacingHttpFailureMessage(error.status, error.message);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Failed to load installed apps';
}

let loadInFlight: Promise<void> | null = null;
let loadInFlightCompanyId: string | null = null;

const initialState = {
  companyId: null as string | null,
  installedAppIds: [] as string[],
  status: 'idle' as CompanyInstalledAppsStatus,
  loadError: null as string | null,
  mutatingAppId: null as string | null,
  mutatingMode: null as 'install' | 'uninstall' | null,
};

export const useCompanyInstalledAppsStore = create<CompanyInstalledAppsState>(
  (set, get) => ({
    ...initialState,

    reset() {
      loadInFlight = null;
      loadInFlightCompanyId = null;
      set({ ...initialState });
    },

    async reload(companyId) {
      const scopedId = resolveScopedCompanyId(companyId);
      if (!scopedId) {
        get().reset();
        return;
      }

      const request = (async () => {
        set({
          companyId: scopedId,
          status: 'loading',
          loadError: null,
        });
        try {
          const response = await fetchCompanyInstalledApps(scopedId);
          if (get().companyId !== scopedId) {
            return;
          }
          set({
            installedAppIds: response.installedAppIds,
            status: 'success',
            loadError: null,
          });
        } catch (error) {
          if (get().companyId !== scopedId) {
            return;
          }
          set({
            status: 'error',
            loadError: toLoadErrorMessage(error),
          });
        }
      })();

      loadInFlight = request;
      loadInFlightCompanyId = scopedId;
      try {
        await request;
      } finally {
        if (loadInFlightCompanyId === scopedId) {
          loadInFlight = null;
          loadInFlightCompanyId = null;
        }
      }
    },

    async ensureLoaded(companyId) {
      const scopedId = resolveScopedCompanyId(companyId);
      if (!scopedId) {
        get().reset();
        return;
      }

      const state = get();
      if (
        state.companyId === scopedId &&
        state.status === 'success' &&
        !loadInFlight
      ) {
        return;
      }

      if (
        loadInFlight &&
        loadInFlightCompanyId === scopedId &&
        state.companyId === scopedId
      ) {
        await loadInFlight;
        return;
      }

      await get().reload(scopedId);
    },

    async installApp(companyId, appId) {
      const scopedId = resolveScopedCompanyId(companyId);
      if (!scopedId) {
        return;
      }
      set({ mutatingAppId: appId, mutatingMode: 'install' });
      try {
        const response = await installCompanyApp(scopedId, appId);
        set({
          companyId: scopedId,
          installedAppIds: response.installedAppIds,
          status: 'success',
          loadError: null,
        });
      } finally {
        set({ mutatingAppId: null, mutatingMode: null });
      }
    },

    async uninstallApp(companyId, appId) {
      const scopedId = resolveScopedCompanyId(companyId);
      if (!scopedId) {
        return;
      }
      set({ mutatingAppId: appId, mutatingMode: 'uninstall' });
      try {
        const response = await uninstallCompanyApp(scopedId, appId);
        set({
          companyId: scopedId,
          installedAppIds: response.installedAppIds,
          status: 'success',
          loadError: null,
        });
      } finally {
        set({ mutatingAppId: null, mutatingMode: null });
      }
    },
  }),
);
